import { http } from "@api/base";
import { invenioConfig } from "@config";
import _get from "lodash/get";
import { sessionManager } from "./SessionManager";

class AuthenticationService {
  static ENDPOINTS = {
    LOGIN: "/login",
    REGISTER: "/api/register",
    LOGOUT: "/logout",
    ME: "/me",
    PROFILE: "/profile",
    USER_PROFILE: "/user/profile",
    CONFIRM_EMAIL: "/confirm-email",
    FORGOT_PASSWORD: "/forgot-password",
    RESET_PASSWORD: "/reset-password",
    SEND_VERIFICATION_EMAIL: "/send-verification-email",
    CHANGE_PASSWORD: "/change-password",
    USER_TYPES: "/api/user-types",
    OPAC: "https://127.0.0.1:3000/",
  };

  static DEFAULT_HEADERS = {
    "Content-Type": "application/json",
    Accept: "application/json",
  };

  static HTTP_CONFIG = {
    withCredentials: true,
    headers: AuthenticationService.DEFAULT_HEADERS,
  };

  constructor() {
    this._configureHttpClient();
  }

  _configureHttpClient() {
    if (http.defaults) {
      http.defaults.withCredentials = true;
    }
  }

  _buildUrl(endpoint) {
    return `${invenioConfig.APP.REST_ENDPOINTS_BASE_URL}${endpoint}`;
  }

  _getCsrfToken() {
    const name = "csrftoken";
    const match = document.cookie.match(new RegExp(`(^|;)\\s*${name}=([^;]+)`));

    if (!match) {
      return null;
    }

    const token = decodeURIComponent(match[2]).trim();

    // Basic token validation
    if (!token || token.length < 10) {
      return null;
    }

    // Optional: Validate token format (adjust regex based on your token format)
    if (!/^[a-zA-Z0-9+/=_-]+$/.test(token)) {
      return null;
    }

    return token;
  }

  _makeRequest(method, endpoint, data = null, customConfig = {}) {
    const url = this._buildUrl(endpoint);
    const config = { ...AuthenticationService.HTTP_CONFIG, ...customConfig };

    if (["post", "put", "delete", "patch"].includes(method.toLowerCase())) {
      const csrfToken = this._getCsrfToken();

      if (csrfToken) {
        if (!config.headers) config.headers = {};
        config.headers["X-CSRFToken"] = csrfToken;
      }
    }

    // Add request ID for tracking (optional)
    if (!config.headers) config.headers = {};
    config.headers["X-Request-ID"] = this._generateRequestId();

    switch (method.toLowerCase()) {
      case "get":
        return http.get(url, config);
      case "post":
        return http.post(url, data, config);
      case "put":
        return http.put(url, data, config);
      case "patch":
        return http.patch(url, data, config);
      case "delete":
        return http.delete(url, config);
      default:
        throw new Error(`Unsupported HTTP method: ${method}`);
    }
  }

  _generateRequestId() {
    return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }

  _validateCSRFSetup() {
    const token = this._getCsrfToken();
    const hasSecureContext =
      window.location.protocol === "https:" ||
      window.location.hostname === "localhost";

    return {
      tokenPresent: !!token,
      secureContext: hasSecureContext,
      recommendations: [
        !token && "CSRF token cookie not found",
        !hasSecureContext && "Consider using HTTPS for production",
      ].filter(Boolean),
    };
  }

  loginWithOauthProvider = (nextUrl, providerUrl) => {
    sessionManager.setAnonymous();
    const redirectOauthUrl = `${_get(
      invenioConfig.APP,
      "INVENIO_UI_URL"
    )}${providerUrl}?next=${encodeURIComponent(nextUrl)}`;
    window.location = redirectOauthUrl;
  };

  loginWithLocalAccount = async (data) => {
    return await this._makeRequest(
      "post",
      AuthenticationService.ENDPOINTS.LOGIN,
      data
    );
  };

  registerWithLocalAccount = (data) => {
    return this._makeRequest(
      "post",
      AuthenticationService.ENDPOINTS.REGISTER,
      data
    );
  };

  logout = async () => {
    try {
      await this._makeRequest("post", AuthenticationService.ENDPOINTS.LOGOUT);
      window.location.href = window.location.origin + "/";
    } catch (error) {
      window.location.href = window.location.origin + "/";
    }
  };

  getCurrentUser = async () => {
    return await this._makeRequest("get", AuthenticationService.ENDPOINTS.ME);
  };

  updateCurrentUser = async (userData) => {
    return await this._makeRequest(
      "put",
      AuthenticationService.ENDPOINTS.ME,
      userData
    );
  };

  confirmUser = (token) => {
    return this._makeRequest(
      "post",
      AuthenticationService.ENDPOINTS.CONFIRM_EMAIL,
      { token }
    );
  };

  requestPasswordReset = (email) => {
    return this._makeRequest(
      "post",
      AuthenticationService.ENDPOINTS.FORGOT_PASSWORD,
      { email }
    );
  };

  resetPassword = (token, password, passwordConfirm) => {
    return this._makeRequest(
      "post",
      AuthenticationService.ENDPOINTS.RESET_PASSWORD,
      {
        token,
        password,
        password_confirm: passwordConfirm,
      }
    );
  };

  changePassword = async (passwordData) => {
    try {
      const response = await this._makeRequest(
        "post",
        AuthenticationService.ENDPOINTS.CHANGE_PASSWORD,
        {
          current_password: passwordData.current_password,
          new_password: passwordData.new_password,
        }
      );
      return response;
    } catch (error) {
      if (error.response) {
        const { status, data } = error.response;
        switch (status) {
          case 400:
            throw new Error(
              data?.message || "Invalid request. Please check your input."
            );
          case 401:
            throw new Error(
              data?.message ||
                "Current password is incorrect. Please try again."
            );
          case 422:
            if (data?.errors) {
              const errorMessages = Object.values(data.errors).flat();
              throw new Error(errorMessages.join(". "));
            }
            throw new Error(
              data?.message || "Password does not meet security requirements."
            );
          case 429:
            throw new Error(
              "Too many password change attempts. Please try again later."
            );
          case 500:
            throw new Error("Server error occurred. Please try again later.");
          default:
            throw new Error(
              data?.message || "Failed to change password. Please try again."
            );
        }
      } else if (error.request) {
        throw new Error(
          "Network error. Please check your connection and try again."
        );
      } else {
        throw new Error(
          error.message || "An unexpected error occurred. Please try again."
        );
      }
    }
  };

  sendEmailVerification = () => {
    return this._makeRequest(
      "post",
      AuthenticationService.ENDPOINTS.SEND_VERIFICATION_EMAIL,
      {}
    );
  };

  getUserProfile = () => {
    return this._makeRequest("get", AuthenticationService.ENDPOINTS.PROFILE);
  };

  updateUserProfile = (profileData) => {
    return this._makeRequest(
      "put",
      AuthenticationService.ENDPOINTS.PROFILE,
      profileData
    );
  };

  updateProfile = (profileData) => {
    return this._makeRequest(
      "post",
      AuthenticationService.ENDPOINTS.USER_PROFILE,
      profileData
    );
  };

  getUserTypes = async () => {
    try {
      const response = await this._makeRequest(
        "get",
        AuthenticationService.ENDPOINTS.USER_TYPES
      );
      return response;
    } catch (error) {
      throw new Error(
        error.response?.data?.error || "Failed to fetch user types"
      );
    }
  };

  hasRoles = (user, roles) => {
    if (!roles.length) {
      return true;
    }
    return roles.some((role) => user.roles?.includes(role));
  };

  validatePasswordStrength = (password) => {
    const requirements = {
      minLength: password.length >= 8,
      hasLowercase: /[a-z]/.test(password),
      hasUppercase: /[A-Z]/.test(password),
      hasNumber: /\d/.test(password),
      hasSpecialChar: /[@$!%*?&]/.test(password),
    };

    const score = Object.values(requirements).filter(Boolean).length;
    const isValid = Object.values(requirements).every(Boolean);

    return {
      isValid,
      score,
      requirements,
      strength: this._getPasswordStrength(score),
    };
  };

  _getPasswordStrength = (score) => {
    if (score <= 2) return "weak";
    if (score <= 4) return "medium";
    return "strong";
  };

  isAuthenticated = () => {
    return sessionManager.isAuthenticated();
  };

  getUserInfo = () => {
    return sessionManager.user;
  };

  // Helper method to validate registration data with user types
  validateRegistrationData = (data) => {
    const errors = [];

    // Basic validation
    if (!data.email) errors.push("Email is required");
    if (!data.password) errors.push("Password is required");
    if (!data.username) errors.push("Username is required");
    if (!data.full_name) errors.push("Full name is required");

    // User type validation
    if (data.member_types && data.member_types.includes("patron")) {
      if (!data.user_type_id) {
        errors.push("User type selection is required for patron registration");
      }
    }

    return {
      isValid: errors.length === 0,
      errors,
    };
  };
}

export const authenticationService = new AuthenticationService();
